import { useLoaderData } from 'react-router';

import { RoccoLink } from '@rocco/components/link';
import type { loader } from '~/routes/_app.project.$urlSegment._index';
import {
    DetailSummaryRow,
    DetailSaveButton,
    detailCta,
} from '~/ui-rocco/pages/detail-shared';
import { useProjectBookMeeting } from '../hooks/use-project-book-meeting';
import {
    ABOUT_PROJECT_ROW_ID,
    useBookMeetingAccordion,
} from '../hooks/use-book-meeting-accordion';
import {
    BOOK_A_MEETING,
    useProjectEnquiryModal,
} from '../hooks/use-project-enquiry-modal';
import { ProjectSupplierTrustRow } from './ProjectSupplierTrustRow';

/**
 * P3 — summary row under the hero. The project description sits on the left; the
 * lead professional credit (logo + name + role) and the Book a Meeting / Visit
 * Website CTAs fill the right `aside` slot of the shared {@link DetailSummaryRow}.
 */
export const ProjectSummarySection = () => {
    const { project, summary } = useLoaderData<typeof loader>();
    const { Professional } = project;

    // Use the loader's truncated summary (HTML stripped, capped before the
    // trailing credit lines that become the P4 team column). The 38-char hard
    // wraps are for the legacy two-column layout — flatten them for this fluid
    // column.
    const description = `${summary.firstCol} ${summary.secondCol}`
        .replace(/\s+/g, ' ')
        .trim();

    const { canBookDirect } = useProjectBookMeeting();
    const { focusBookMeeting } = useBookMeetingAccordion();
    // Fallback when the project has no direct-booking session: the "Book a
    // Meeting" CTA opens the standard project enquiry modal instead.
    const { openEnquiryModal } = useProjectEnquiryModal();
    const role = Professional.Categories?.[0]?.PrettyTitle;
    const onBookMeeting = canBookDirect
        ? focusBookMeeting
        : () =>
              openEnquiryModal({
                  title: BOOK_A_MEETING,
                  ctaLabel: BOOK_A_MEETING,
              });

    return (
        <DetailSummaryRow
            theme="dark"
            // No "Introducing {title}" caption — the hero already states the
            // title right above; the eyebrow just repeated it.
            description={description || undefined}
            clampLines={3}
            // "Read more" scrolls to the (01) About-the-Project row (the same
            // copy in full) rather than expanding the summary in place.
            readMoreAnchor={`#${ABOUT_PROJECT_ROW_ID}`}
            aside={
                <>
                    {/* Mobile (QDD-first): a full-width primary Book-a-Meeting
                        pill over a paired Visit Website / Save row, then the
                        trust echo — the same cluster shape as the product PDP. */}
                    <div className="flex w-full flex-col gap-3 md:hidden">
                        <button
                            type="button"
                            onClick={onBookMeeting}
                            className={detailCta(
                                'filled-invert',
                                'h-[3.125rem] w-full text-sm',
                            )}
                        >
                            {BOOK_A_MEETING}
                        </button>
                        <div className="flex items-stretch gap-2">
                            {Professional.Website ? (
                                <RoccoLink
                                    to={Professional.Website}
                                    newTab
                                    className={detailCta(
                                        'outlined-dark',
                                        'h-[3.125rem] flex-1 text-sm',
                                    )}
                                >
                                    Visit Website
                                </RoccoLink>
                            ) : null}
                            <DetailSaveButton
                                pinId={project.ID}
                                pinType="ProjectPin"
                                theme="dark"
                                className="h-[3.125rem] w-[3.125rem] shrink-0"
                            />
                        </div>
                        <ProjectSupplierTrustRow />
                    </div>

                    {/* Desktop: right-aligned credit above the inline CTA row. */}
                    <RoccoLink
                        to={Professional.Link}
                        // mt-[4.75rem] gives the right column the Figma's 76px top
                        // padding (2354:55422 pt-[76px]); mb adds to the aside's
                        // 24px gap → 32px name/role→CTA gap (right column gap-[32px]).
                        className="min-w-0 transition-opacity hover:opacity-80 max-md:hidden md:mt-[4.75rem] md:mb-[0.5rem] md:text-right"
                    >
                        <p className="m-0 font-sans leading-[1.3] font-normal text-white md:text-[1.5rem]">
                            {Professional.Title}
                        </p>
                        {role ? (
                            <p className="m-0 font-sans leading-[1.3] font-normal text-white/50 md:text-[1.5rem]">
                                {role}
                            </p>
                        ) : null}
                    </RoccoLink>
                    <div className="flex items-center gap-2 max-md:hidden">
                        <button
                            type="button"
                            onClick={onBookMeeting}
                            className={detailCta('filled-dark')}
                        >
                            {BOOK_A_MEETING}
                        </button>
                        {Professional.Website ? (
                            <RoccoLink
                                to={Professional.Website}
                                newTab
                                className={detailCta('outlined-dark')}
                            >
                                Visit Website
                            </RoccoLink>
                        ) : null}
                        <DetailSaveButton
                            pinId={project.ID}
                            pinType="ProjectPin"
                            theme="dark"
                        />
                    </div>
                </>
            }
        />
    );
};
